import type { HubConnectionState } from "@microsoft/signalr";



import type { IUsernamePasswordAuthCmd } from "@/data/commands/auth/UsernamePasswordAuthCmd";
import type { ISetOperatorSettingsCmd } from "@/data/commands/user/SetOperatorSettingsCmd";
import { Factory } from "@/data/dao/Factory";
import { CookieCache } from "@/data/dao/offline/CookieCache";
import { AuthenticationDao } from "@/data/dao/service/AuthenticationDao";
import { ProfileDao } from "@/data/dao/service/ProfileDao";
import { OperatorSocket } from "@/data/dao/socket/OperatorSocket";
import { dataEvents } from "@/data/events/dataEvents";
import { OperatorItemAvailabilityAndStockLevelClaimValue } from "@/data/model/Enums";
import type { IProfile } from "@/data/model/Profile";
import type { IOperatorSettings } from "@/data/model/User";
import { config, role } from "@/data/util/Config";
import { Logging } from "@/data/util/Logging";



import { logError } from "@/shared/logging/errors";
import { DeviceFingerprintProvider } from "@/shared/services/DeviceFingerprintProvider";
import { isRunnningInNativeMode, isWalleeSdkAvailable, setBearerToken } from "@/shared/utils/androidNativeUtils";
import { objectToArray } from "@/shared/utils/mappers";



import { ConnectionState } from "@/components/websocket/ConnectionState";



import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";


export class Operator {
    private profileDao = Factory.getInstance<ProfileDao>(ProfileDao);
    private authenticationDao = Factory.getInstance<AuthenticationDao>(AuthenticationDao);
    private cookieCache = Factory.getInstance<CookieCache>(CookieCache);

    public profile?: IProfile;
    public settings?: IOperatorSettings;

    public socket: OperatorSocket;

    private nativeAppMode: boolean = false;
    private isWalleeSdkAvailable: boolean | undefined = undefined;

    constructor() {
        this.nativeAppMode = isRunnningInNativeMode();
        this.isWalleeSdkAvailable = isWalleeSdkAvailable();

        this.socket = new OperatorSocket();
        this.socket.onConnectionChanged.add(this.handleConnectionChanged, this);
        this.socket.onReconnected.add(this.handleReconnected, this);

        dataEvents.operatorSocket.OperatorUserSignedOut.add(this.handleUserSignedOut, this);

        try {
            if (window["jQuery"] != null && window["jQuery"].fn["selectpicker"] != null) {
                window["jQuery"].fn["selectpicker"].defaults = { noneSelectedText: trans.general.select };
            }
        } catch {
            // ignore
        }
    }

    public getNativeAppMode(): boolean {
        return this.nativeAppMode;
    }

    public getOperatorItemAvailabilityAndStockLevelGrant(): OperatorItemAvailabilityAndStockLevelClaimValue | undefined {
        return this.profile?.operatorItemAvailabilityAndStockLevelGrant;
    }

    public getIsWalleeSdkAvailable(): boolean | undefined {
        return this.isWalleeSdkAvailable;
    }

    public async init(): Promise<void> {
        try {
            if (!this.hasPreviousSession()) {
                return;
            }

            // setup user profile and settings
            this.profile = await this.profileDao.getProfile();
            await this.updateSettings();

            // connect to web socket
            await this.connectSocket();

            // setup current location
            await this.updateLocation(this.settings?.location?.id);
        } catch (ex) {
            logError(ex);
            await this.resetAuthentication();
        } finally {
            operatorEvents.context.loaded.dispatch();
        }
    }

    public async signOut(): Promise<void> {
        operatorEvents.context.signingOut.dispatch();

        // disconnect socket and mark as disabled - so no information about connection lost will be displayed
        await this.disconnectSocket();

        // logout from server
        await this.authenticationDao.signOut();

        // reset authentication context - profile and settings
        await this.resetAuthentication();

        // notify about authentication changed
        operatorEvents.context.authenticationChanged.dispatch(false);
    }

    public async authenticateWithCredentials(credentials: IUsernamePasswordAuthCmd): Promise<void> {
        // set browser fingerprint - required to map device to identity
        credentials.fingerprint = await DeviceFingerprintProvider.getFingerprint();

        // set scope to operator app
        credentials.scope = "operator";

        // get bearer token
        const token = await this.authenticationDao.auth(credentials);

        // set bearer token on native app
        if (isRunnningInNativeMode()) {
            setBearerToken(token.bearer);
        }

        // setup user profile and settings
        this.profile = await this.profileDao.getProfile();

        await this.updateSettings();

        // connect to web socket
        await this.connectSocket();

        // setup current location
        await this.updateLocation(this.settings?.location?.id);

        // notify user is authenticated
        if (this.isAuthenticated()) {
            operatorEvents.context.authenticationChanged.dispatch(true);
        }
    }

    public isAuthenticated(): boolean {
        return this.profile != null && this.isAuthorized();
    }

    public async connectSocket() {
        await this.socket.connect();
        ConnectionState.disabled = false;
    }

    public reactivateSocket() {
        this.socket.checkConnectivity();
    }

    public async disconnectSocket() {
        ConnectionState.disabled = true;
        await this.socket.disconnect();
    }

    public hasMissingSettings(): boolean {
        if (this.settings === undefined) return true;

        if (this.settings?.location?.id === undefined || this.settings.planRoom === undefined) return true;

        return false;
    }

    public async changeSettings(settings: ISetOperatorSettingsCmd): Promise<IOperatorSettings | undefined> {
        const newSettings = await this.profileDao.setOperatorSettings(settings);
        const hasLocationChanges =
            this.settings === undefined || this.settings.location?.id !== newSettings?.location?.id;

        await this.setNewSettings(newSettings);

        await this.updateSettings();

        if (hasLocationChanges && this.settings?.location?.id !== undefined)
            await this.updateLocation(this.settings?.location?.id);

        operatorEvents.context.settingsChanged.dispatch(this.settings);
        return newSettings;
    }

    public isAdmin() {
        const locationId = this.settings?.location?.id;
        if (this.profile == null || locationId == null) {
            return false;
        }
        const isAdmin = this.userIsInRole(role.Admin);

        return isAdmin;
    }

    public isAuthorized() {
        const isWaiter = this.userIsInRole(role.Operator);
        const isAdmin = this.userIsInRole(role.Admin);

        return isWaiter || isAdmin;
    }

    private userIsInRole(role: string) {
        return objectToArray(this.profile?.roles).includes(role);
    }

    private async updateSettings() {
        const settings = await this.profileDao.getOperatorSettings();
        this.settings = settings;

        operatorEvents.context.settingsUpdated.dispatch(settings);
    }

    private async setNewSettings(newSettings: IOperatorSettings | undefined) {
        this.settings = newSettings;
        operatorEvents.context.settingsUpdated.dispatch(newSettings);
    }

    private async updateLocation(newLocationId: string | undefined): Promise<void> {
        operatorEvents.context.locationChanged.dispatch();

        if (newLocationId && this.socket.connectionId) {
            Logging.LogSocketNotification(`Listen to location: ${newLocationId}`);
            await this.socket.listenToLocation(newLocationId);
        }
    }

    private async resetAuthentication() {
        await this.setNewSettings(undefined);
        this.profile = undefined;
        ConnectionState.disabled = true;

        // set bearer token on native app
        if (isRunnningInNativeMode()) {
            setBearerToken(null);
        }
    }

    private hasPreviousSession(): boolean {
        return this.cookieCache.readString(config.appSessionCookieName) != null;
    }

    private handleConnectionChanged(state: HubConnectionState) {
        ConnectionState.setBySignalR(state);
    }

    private async handleReconnected() {
        console.log("socket.onReconnected");
        const locationId = this.settings?.location?.id;

        if (locationId) {
            Logging.LogSocketNotification(
                `Connecting new connection id ${this.socket.connectionId} to location ${locationId}`
            );
            await this.socket.listenToLocation(locationId);
        }
    }

    private async handleUserSignedOut() {
        await this.resetAuthentication();
        operatorEvents.context.authenticationChanged.dispatch(false);
    }
}
